import { Component, Input, OnInit, OnChanges, SimpleChanges, inject, signal, computed, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AuditService } from '../../../core/services/audit.service';
import { AuditLog, AuditEntityType, GetAuditLogsQuery, AuditAction } from '../../../core/models/audit-log.model';
import { IconComponent, IconName } from '../../icons/icon.component';
import { PaginationComponent } from '../pagination/pagination.component';
import { DropdownComponent, DropdownOption } from '../ui/dropdown/dropdown.component';
import { ApiService } from '../../../core/services/api.service';
import { FormsModule } from '@angular/forms';
import { DatePickerComponent, DateRangePickerOutput } from '../date-picker/date-picker.component';
import { ButtonComponent } from '../ui/button/button.component';
import { AuthService } from '../../../core/services/auth.service';


@Component({
  selector: 'app-audit-log-panel',
  standalone: true,
  imports: [CommonModule, IconComponent, PaginationComponent, DropdownComponent, FormsModule, DatePickerComponent, ButtonComponent],
  templateUrl: './audit-log-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './audit-log-panel.component.scss'
})
export class AuditLogPanelComponent implements OnInit, OnChanges {
  @ViewChild(DatePickerComponent) datePicker!: DatePickerComponent;
  private auditService = inject(AuditService);
  private apiService = inject(ApiService);
  private authService = inject(AuthService);


  @Input({ required: true }) entityType!: AuditEntityType | AuditEntityType[];
  @Input() productPid?: number;
  @Input() limit: number = 10;
  @Input() title: string = 'Audit Logs';
  @Input() showTitle: boolean = true;
  @Input() showProductFilter: boolean = true;
  /** When true, the panel starts expanded instead of collapsed. */
  @Input() expanded: boolean = false;

  isExpanded = signal(false);
  isFilterVisible = signal(false);
  hasAccess = signal(true);


  // Filter States
  filterProductPid = signal<number | null>(null);
  filterAction = signal<string[]>([]);
  filterDateRange = signal<{ start: string | null, end: string | null }>({ start: null, end: null });

  isDateFilterActive = signal(false);
  private isFirstDateEmission = true;

  isFiltersApplied = computed(() => {
    const productApplied = !!this.filterProductPid();
    const actionApplied = this.filterAction().length > 0;
    const dateApplied = this.isDateFilterActive();

    return productApplied || actionApplied || dateApplied;
  });

  productOptions = computed<DropdownOption[]>(() =>
    this.apiService.products().map(p => ({ label: p.name, value: String(p.pid) }))
  );

  actionOptions: DropdownOption[] = [
    { label: 'Create', value: 'CREATE', icon: 'plus' },
    { label: 'Update', value: 'UPDATE', icon: 'pencil' },
    { label: 'Delete', value: 'DELETE', icon: 'trash' },
  ];

  auditLogs: AuditLog[] = [];
  totalItems: number = 0;
  currentPage: number = 1;
  isLoading: boolean = false;

  ngOnInit(): void {
    if (this.expanded) this.isExpanded.set(true);
    this.fetchAuditLogs();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['entityType'] && !changes['entityType'].firstChange) ||
      (changes['productPid'] && !changes['productPid'].firstChange)) {
      this.currentPage = 1;
      this.fetchAuditLogs();
    }
  }

  fetchAuditLogs(): void {
    this.isLoading = true;
    const dateRange = this.filterDateRange();

    const query: GetAuditLogsQuery = {
      entityType: Array.isArray(this.entityType) ? this.entityType : [this.entityType],
      productPid: this.filterProductPid() || this.productPid,
      action: this.filterAction().length > 0 ? this.filterAction() as AuditAction[] : undefined,
      startDate: this.isDateFilterActive() ? dateRange.start || undefined : undefined,
      endDate: this.isDateFilterActive() ? dateRange.end || undefined : undefined,
      page: this.currentPage,
      limit: this.limit,
      orderBy: 'createdAt',
      orderDirection: 'desc'
    };

    this.auditService.getAuditLogs(query).subscribe({
      next: (res) => {
        // Permission check
        if (res.permissions && res.permissions.keyValue) {
          const userRolePid = this.authService.currentUser()?.['rolePid'];
          const allowedRoles = res.permissions.keyValue;
          this.hasAccess.set(allowedRoles.includes(Number(userRolePid)));
        } else {

          this.hasAccess.set(true); // Default to true if no permissions object
        }

        this.auditLogs = res.items || [];
        this.totalItems = res.meta?.total || 0;
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error fetching audit logs:', err);
        this.isLoading = false;
      }
    });
  }

  toggleExpand() {
    this.isExpanded.update((v: boolean) => !v);
  }

  toggleFilter(event?: Event) {
    if (event) event.stopPropagation();
    this.isFilterVisible.update(v => !v);
    if (this.isFilterVisible() && !this.isExpanded()) {
      this.isExpanded.set(true);
    }
  }

  onProductChange(pid: any) {
    const numericPid = (pid !== null && pid !== undefined && pid !== '') ? Number(pid) : null;
    this.filterProductPid.set(numericPid);
  }

  onActionChange(actions: any) {
    this.filterAction.set(Array.isArray(actions) ? actions : (actions ? [actions] : []));
  }

  onDateChange(event: DateRangePickerOutput) {
    const range = {
      start: event.start_date,
      end: event.end_date
    };

    if (this.isFirstDateEmission) {
      this.isFirstDateEmission = false;
      this.isDateFilterActive.set(false);
    } else {
      this.isDateFilterActive.set(true);
    }

    this.filterDateRange.set(range);
  }

  applyFilters() {
    this.reloadLogs();
  }

  reloadLogs() {
    this.currentPage = 1;
    this.fetchAuditLogs();
  }

  clearFilters() {
    this.filterProductPid.set(null);
    this.filterAction.set([]);
    this.isDateFilterActive.set(false);
    this.isFirstDateEmission = true;
    this.datePicker?.reset();
    this.reloadLogs();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.fetchAuditLogs();
  }

  getActionIcon(action: string): IconName {
    switch (action) {
      case 'CREATE': return 'plus';
      case 'UPDATE': return 'pencil';
      case 'DELETE': return 'trash';
      case 'ACTIVATE': return 'check';
      case 'DEACTIVATE': return 'x';
      default: return 'info';
    }
  }

  getActionColor(action: string): string {
    // We use consistent neutral colors for a cleaner, sidebar-like look
    return 'text-slate-500 dark:text-slate-300 bg-slate-100/80 dark:bg-slate-800/80';
  }

  getUpdateMainId(log: AuditLog): { key: string, value: any } | null {
    if (log.action !== 'UPDATE') return null;
    const oldVal = log.oldValue || {};
    const newVal = log.newValue || {};

    if (newVal.pid || oldVal.pid) {
      return { key: 'PID', value: newVal.pid || oldVal.pid };
    }
    if (newVal.id || oldVal.id) {
      return { key: 'ID', value: newVal.id || oldVal.id };
    }
    return null;
  }

  getUpdateChanges(log: AuditLog): { key: string, old: any, new: any }[] {
    const isConsolidatedPermission = log.entityType === 'ROUTE_PERMISSION' && Array.isArray(log.oldValue) && Array.isArray(log.newValue);
    if ((log.action !== 'UPDATE' && !isConsolidatedPermission) || !log.oldValue || !log.newValue) return [];

    const oldVal = log.oldValue;
    const newVal = log.newValue;
    const excludeKeys = ['id', 'pid', 'createdAt', 'updatedAt', 'deletedAt', 'createdBy', 'updatedBy', 'routeId', 'routePid', 'routeName'];

    // Hide roleId/rolePid for ROUTE_PERMISSION to avoid noise, but show them for general updates
    if (log.entityType === 'ROUTE_PERMISSION') {
      excludeKeys.push('roleId', 'rolePid');
    }

    // Special handling for ROUTE_PERMISSION consolidated logs (Arrays)
    if (log.entityType === 'ROUTE_PERMISSION' && Array.isArray(oldVal) && Array.isArray(newVal)) {
      const results: { key: string, old: any, new: any }[] = [];

      // Since it's consolidated, oldVal[i] corresponds to newVal[i]
      for (let i = 0; i < newVal.length; i++) {
        const oldItem = oldVal[i] || {};
        const newItem = newVal[i];

        const routeLabel = newItem.routeName || `Module Route (PID: ${newItem.routePid})`;
        results.push({
          key: routeLabel,
          old: oldItem.hasAccess === true ? 'Granted' : (oldItem.hasAccess === false ? 'Revoked' : 'None'),
          new: newItem.hasAccess === true ? 'Granted' : 'Revoked'
        });
      }
      return results;
    }

    // Handle Objects
    if (this.isObject(oldVal) && this.isObject(newVal)) {
      const oldKeys = Object.keys(oldVal);
      const newKeys = Object.keys(newVal);

      // Only show fields that exist on BOTH sides
      const commonKeys = oldKeys.filter(k => newKeys.includes(k));

      return commonKeys
        .filter(key => !excludeKeys.includes(key))
        .filter(key => {
          const oldStr = JSON.stringify(oldVal[key]);
          const newStr = JSON.stringify(newVal[key]);
          return oldStr !== newStr;
        })
        .map(key => {
          let oldDisplay = oldVal[key];
          let newDisplay = newVal[key];

          // If the field is an object (like 'role') and has a 'name', display that cleanly
          if (this.isObject(oldDisplay) && this.isObject(newDisplay) && oldDisplay.name && newDisplay.name) {
            oldDisplay = oldDisplay.name;
            newDisplay = newDisplay.name;
          } else {
            oldDisplay = this.formatValue(oldDisplay);
            newDisplay = this.formatValue(newDisplay);
          }

          return {
            key,
            old: oldDisplay,
            new: newDisplay
          };
        });
    }

    // Handle Arrays or other JSON structures if they differ
    if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
      return [{
        key: 'data',
        old: this.formatValue(oldVal),
        new: this.formatValue(newVal)
      }];
    }

    return [];
  }

  private isObject(val: any): boolean {
    return val !== null && typeof val === 'object' && !Array.isArray(val);
  }

  private formatValue(val: any): string {
    if (val === null || val === undefined) return 'null';
    if (typeof val === 'object') {
      return JSON.stringify(val);
    }
    return String(val);
  }
}
